import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

/**
 * Small, keyboard-accessible "what is this" affordance (UX-HELP-001).
 *
 * Deliberately a separate focusable control next to a nav Link/button
 * rather than nested inside it — nesting an interactive element inside
 * another interactive element (e.g. a button inside an <a>) is invalid
 * HTML and breaks keyboard/screen-reader navigation. Opens on click/Enter
 * (not hover-only), so it works on touch and for keyboard users.
 *
 * `content` shape: { what, does, when?, role?, readOnly?, external? }
 */
export default function HelpInfo({ label, content }) {
  if (!content) return null;

  return (
    <Popover className="relative shrink-0">
      <PopoverButton
        type="button"
        aria-label={`עזרה: ${label}`}
        title={`עזרה: ${label}`}
        className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] leading-none text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        <span aria-hidden="true">ⓘ</span>
      </PopoverButton>
      <PopoverPanel
        anchor="left start"
        portal
        dir="rtl"
        className="z-50 w-72 rounded-lg bg-white text-gray-800 shadow-lg border border-gray-200 p-3 text-xs leading-relaxed [--anchor-gap:10px]"
      >
        <p className="font-semibold text-sm mb-1.5">{label}</p>
        <p className="mb-1">{content.what}</p>
        <p className="mb-1">{content.does}</p>
        {content.when && (
          <p className="text-gray-500 mb-1">
            <span className="font-medium text-gray-600">מתי להשתמש: </span>
            {content.when}
          </p>
        )}
        {content.role && <p className="text-gray-500 mb-1">{content.role}</p>}
        {typeof content.readOnly === "boolean" && (
          <p className="text-gray-500">
            {content.readOnly
              ? "תצוגה בלבד — לא מבצעת פעולות."
              : "מאפשר ביצוע פעולות, לא רק צפייה."}
          </p>
        )}
        {content.external && (
          <p className="text-gray-500">נפתח מחוץ למערכת Prodigy, בכרטיסייה חדשה.</p>
        )}
      </PopoverPanel>
    </Popover>
  );
}
